import { SelectionModel } from '@angular/cdk/collections';
import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DocumentDataSource } from 'src/app/acl-data/document/document.datasource';
import { Document } from 'src/app/acl-data/document/document.dto';
import { DocumentService } from 'src/app/acl-data/document/document.service';
import { DocumentPreviewComponent } from 'src/app/document-views/document-preview/document-preview.component';
import { SubSink } from 'subsink';

import { SearchResultDTO } from '../search-result.dto';
import { environment } from 'src/environments/environment';
import { AssignLegalCounselDialogComponent } from '../assign-legal-counsel-dialog/assign-legal-counsel-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ObjectStoreService } from 'src/app/acl-data/object-store/object-store.service';

@Component({
    selector: 'app-search-results',
    templateUrl: './search-results.component.html',
    styleUrls: ['./search-results.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsComponent implements OnChanges, OnDestroy {
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    @Input() documentIds: SearchResultDTO[];
    @Input() objectStore: string;
    @Input() resultsExceeded = false;

    @Output() DocumentSelected = new EventEmitter<string[]>();
    @Output() DownloadResults = new EventEmitter<any>();

    @Input() fullTextResults = false;

    subSink = new SubSink();

    userRoles$ = this.objectStoreService.entities$.pipe(
        map(v => v.find(row => row.symbolicName === this.objectStore)));

    documentIds$ = new BehaviorSubject<SearchResultDTO[]>([]);

    loading$ = this.documentService.loading$;
    downloading$ = new BehaviorSubject(false);
    downloadTimedOut = false;

    docs$ = this.documentIds$.pipe(
        switchMap(v =>
            this.documentService.getByIds(
                this.objectStore,
                v.map(row => row.id),
                false,
                true
            )
        )
    );

    documents$ = this.docs$.pipe(map(v => v.map(row => row.id)));

    linkedDocuments$ = this.docs$.pipe(
        // tap(v => console.log('Linked Documents', v)),
        map(v =>
            v.reduce(
                (acc, row) => [
                    ...acc,
                    ...(!!row.linkedDocuments ? row.linkedDocuments : []),
                ],
                []
            )
        ),
        switchMap(v => this.documentService.getByIds(this.objectStore, v)),
        map(v =>
            v.reduce((acc, row) => {
                acc[row.id] = row.propertiesObj.ContractIdentifier;
                return acc;
            }, {})
        )
        // tap(v => console.log('V', v))
    );

    dataSource;
    snakbarRef: MatSnackBarRef<any>;

    displayedColumns: string[] = [
        'select',
        'ContractDeletedDate',
        'Name',
        'ContractIdentifier',
        'ContractStartDate',
        'ContractEndDate',
        'ContractLibrary',
        'ContractCategory',
        'ContractDateOfLastSignature',
        'ContractReviewDeadlineDate',
        'DateCreated',
        'LinkedDocuments',
        'ContractStatus',
        'Creator',
        'ContractAgreementType',
        'ContractBusinessOwner',
        'ContractCMSID',
    ];

    selection = new SelectionModel<Document>(true, []);

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    getSelected = () => {
        const results = this.selection.selected.map(v => v.id);
        return results;
    };

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        this.isAllSelected()
            ? this.selection.clear()
            : this.dataSource.data.forEach(row => this.selection.select(row));
    }

    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: Document): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
            row.propertiesObj.DocumentTitle
        }`;
    }

    paged(input: PageEvent) {
        this.dataSource.pageChange(input);
    }

    applySort(input: Sort) {
        this.dataSource.sortChanged(input);
    }

    applyFilter(filterValue: string) {
        this.dataSource.filterChange(filterValue.trim().toLowerCase());

        if (this.paginator) {
            this.paginator.firstPage();
        }
    }

    closeDialog = () => {
        if (this.snakbarRef) {
            this.snakbarRef.dismiss();
        }
    };

    preview(doc: Document) {
        if (this.snakbarRef) {
            this.snakbarRef.dismiss();
        }
        this.snakbarRef = this.snackBar.openFromComponent(
            DocumentPreviewComponent,
            {
                duration: 30000,
                data: { document: doc, objectStore: this.objectStore },
                horizontalPosition: 'right',
            }
        );
    }

    assignLegalCounsel  = () => {
        console.log("Opening AssignLegalCounselDialog")
        var ids:String[] = []
        for (let index = 0; index < this.selection.selected.length; index++) {
            ids[index] = this.selection.selected[index].id
        }
        const dialogRef = this.assignLegalCounselDialog.open(AssignLegalCounselDialogComponent, {
            data: {ids:ids, objectStore: this.objectStore},   
        });

        dialogRef.afterClosed().subscribe(result => {
            console.log("AssignLegalCounselDialog state = " + result.state);
            if (result.state == "success") {
                this.snackBar.open('The selected contracts have been successfully updated to ' + result.contractLegalCounselName, 'Dismiss');
            } else if (result.state == "error") {
                this.snackBar.open('The select contracts could not be updated. Please contact Support for assistance.', 'Dismiss');
            }
        });
    };

    ngOnDestroy(): void {
        this.subSink.unsubscribe();
    }

    constructor(
        private documentService: DocumentService,
        private snackBar: MatSnackBar,
        private httpClient: HttpClient,
        private cbr: ChangeDetectorRef,
        private assignLegalCounselDialog: MatDialog,
        private objectStoreService: ObjectStoreService
    ) {
        this.subSink.add(
            this.selection.changed.subscribe(v =>
                this.DocumentSelected.emit(
                    this.selection.selected.map(v => v.id)
                )
            )
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.documentIds$.next(this.documentIds);
        this.selection.clear();

        if (this.paginator) {
            this.paginator.firstPage();
        }

        if (!this.dataSource) {
            this.dataSource = new DocumentDataSource(
                this.documentService,
                this.documents$,
                this.objectStore,
                this.paginator
            );
        }
    }

    getSummaryData = (id: string) => {
        const row = this.documentIds.find(v => v.id === id);
        if (!!row) {
            return this.formatSumarry(row.contentSummary, row.highlightOffsets);
        } else {
            return '';
        }
    };

    formatSumarry = (summary: string, offsets: number[]) => {
        // console.log(summary, offsets);
        if (!summary || !offsets) {
            return 'Not Available';
        }

        offsets = offsets.sort((n1, n2) => (n1 > n2 ? -1 : 1)); // Sort offsets descending order

        let closingTag = true;
        offsets.forEach(v => {
            summary = `${summary.slice(0, v)}${
                closingTag ? '</mark>' : '<mark>'
            }${summary.slice(v)}`;
            closingTag = !closingTag;
        });

        while (summary.indexOf('\n') > 0) {
            summary = summary.replace('\n', '<br>');
        }

        return summary;
    };

    download = () => {
        this.downloading$.next(true);
        const ids = this.selection.selected.map(v => v.id);
        console.log('Downloading', ids);

        this.httpClient
            .post(
                `${environment.serviceURL_ACL}/getZip/${this.objectStore}`,
                ids,
                {
                    responseType: 'arraybuffer',
                    headers: { Accept: 'application/zip' },
                }
            )
            .subscribe(
                response => {
                    this.downLoadFile(response, 'application/zip');
                    this.downloading$.next(false);
                },
                error => {
                    console.warn('Download Error');
                    this.downloading$.next(false);
                    this.downloadTimedOut = true;
                    this.cbr.detectChanges();
                }
            );
    };

        /**
     * Method is use to download file.
     * @param data - Array Buffer data
     * @param type - type of the document.
     */
         downLoadFile(data: any, typeValue: string) {
            console.log('Download Data');
            const blob = new Blob([data], { type: typeValue });
            const url = window.URL.createObjectURL(blob);
            window.location.href = url;
            // const pwa = window.open(url);
            // if (!pwa || pwa.closed || typeof pwa.closed == 'undefined') {
            //     alert('Please disable your Pop-up blocker and try again.');
            // }
        }
}
